import { useEffect, useState } from "react";
import {
  Select,
  Row,
  Col,
  Typography,
  Button,
  message,
  Skeleton,
  Table,
  Tag,
} from "antd";
import {
  createPricingDealFromTemplate,
  getAllDealsInfo,
  getHubspotDealDetails,
} from "../../services/Apis";
import LogoutPopUp from "../../components/LogoutPopUp";
import type { ColumnsType } from "antd/es/table";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const { Option } = Select;
const { Text, Title } = Typography;
interface hubspotDeals {
  deal_id: number;
  deal_name: string;
  deal_owner_name: string;
  can_create_pricing: boolean;
  project_start_date: string | null;
  duration_in_months: number | null;
  hubspot_link: string | null;
  company_name: string | null;
  engagement_type: string | null;
  has_pricing_deal?: boolean | null;
}

export default function LinkTemplateToHubspot({
  pricingTemplateData,
  dealId,
  closeModal,
  templateId,
}: {
  pricingTemplateData: any;
  dealId?: number;
  closeModal: any;
  templateId?: number;
}) {
  const navigate = useNavigate();
  const [hubspotDeals, setHubspotDeals] = useState<hubspotDeals[]>([]);
  const [createDealError, setCreateDealError] = useState<string | null>(null);
  const [selectedDealId, setSelectedDealId] = useState<number | null>(
    dealId ?? null
  );
  const [tokenExpired, setTokenExpired] = useState<boolean>(false);
  const [dataFetching, setDataFetching] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedDealDetails, setSelectedDealDetails] =
    useState<hubspotDeals | null>(null);

  const generateDiffData = (
    pricingTemplateData: any,
    selectedDealId: number
  ) => {
    const selectedDeal = hubspotDeals.filter(
      (deal) => deal.deal_id == selectedDealId
    );
    return [
      {
        property: "Duration",
        pricingValue: pricingTemplateData["Duration"],
        hubspotValue: selectedDeal[0]["duration_in_months"],
      },
      {
        property: "Engagement Type",
        pricingValue: pricingTemplateData["Engagement Type"],
        hubspotValue: selectedDeal[0]["engagement_type"],
      },
      {
        property: "Expected Start Date",
        pricingValue: pricingTemplateData["Expected Start Date"],
        hubspotValue: selectedDeal[0]["project_start_date"]?.split("T")[0],
      },
    ];
  };

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "property",
      key: "property",
      align: "center",
      render: (text: string) => (
        <span style={{ fontWeight: "bold" }}>{text}</span>
      ),
    },
    {
      title: "Value From HubSpot",
      dataIndex: "hubspotValue",
      key: "hubspotValue",
      align: "center",
    },
    {
      title: "Value From Pricing Template",
      dataIndex: "pricingValue",
      key: "pricingValue",
      align: "center",
    },
  ];

  const createDealPricingFromTemplate = async () => {
    if (!selectedDealId) {
      message.error("Please select a HubSpot Deal");
      return;
    }
    if (!templateId) {
      message.error("Invalid Template Id");
      return;
    }
    setSubmitting(true);
    const createPricing = await createPricingDealFromTemplate(
      selectedDealId,
      templateId
    );
    setSubmitting(false);
    if (createPricing.error_status === 401) {
      setTokenExpired(true);
    } else {
      if (createPricing.error) {
        console.error("Error Response Data:", createPricing.error);
        console.error("Error Response Status:", createPricing.error_status);
        setCreateDealError(createPricing.error);
        message.error(createPricing.error);
      } else {
        navigate(
          `/edit-pricing-details?dealId=${selectedDealId}&versionName=${encodeURIComponent(
            createPricing.version_name
          )}`
        );
      }
    }
  };

  useEffect(() => {
    setSelectedDealDetails(
      hubspotDeals.filter((deal) => deal.deal_id == selectedDealId)[0]
    );
  }, [selectedDealId]);

  useEffect(() => {
    const getDeals = async () => {
      setDataFetching(true);
      const getDealsResponse = await getAllDealsInfo();
      setDataFetching(false);
      if (getDealsResponse.error_status === 401) {
        setTokenExpired(true);
      } else {
        if (getDealsResponse.error) {
          console.error("Error Response Data:", getDealsResponse.error);
          console.error(
            "Error Response Status:",
            getDealsResponse.error_status
          );
          setCreateDealError(getDealsResponse.error);
        } else {
          setHubspotDeals(getDealsResponse);
        }
      }
    };

    const getDeal = async (deal_id: number) => {
      setDataFetching(true);
      const getDealResponse = await getHubspotDealDetails(deal_id);
      setDataFetching(false);
      if (getDealResponse.error_status === 401) {
        setTokenExpired(true);
      } else {
        if (getDealResponse.error) {
          console.error("Error Response Data:", getDealResponse.error);
          console.error("Error Response Status:", getDealResponse.error_status);
          setCreateDealError(getDealResponse.error);
        } else {
          setHubspotDeals([getDealResponse]);
          setSelectedDealDetails(getDealResponse);
        }
      }
    };
    if (dealId) {
      getDeal(dealId);
    } else {
      getDeals();
    }
  }, []);

  return dataFetching ? (
    <Skeleton active style={{ padding: "5%" }} paragraph={{ rows: 6 }} />
  ) : tokenExpired ? (
    <LogoutPopUp openPopup={tokenExpired} />
  ) : (
    <Row gutter={16}>
      <Col span={24}>
        <strong style={{ fontSize: "16px" }}>
          Link Pricing Template To a HubSpot Deal
        </strong>
        <p>Linking uses the last saved template. Save any changes before linking. Once linked, this template becomes read-only; you can clone it to make further changes.</p>
      </Col>
      {createDealError && <Text type="danger">{createDealError}</Text>}
      {!dealId && (
        <>
          <Col span={24}>
            <div style={{ marginBottom: "0px", marginTop: "12px" }}>
              <div>
                <strong>Select a HubSpot Deal</strong>
              </div>
            </div>
            <div style={{ marginBottom: "2px" }}>
              <Text type="secondary">
                Search by Client, Deal Name, HubSpot ID, or Deal Owner
              </Text>
            </div>
          </Col>
          <Col span={21}>
            <Select
              showSearch
              placeholder="HubSpot Deal"
              style={{ width: "100%" }}
              filterOption={(input, option: any) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
              onSelect={(value) => setSelectedDealId(value)}
            >
              {hubspotDeals
                .filter((option) => option.can_create_pricing)
                .map((option, index) => (
                  <Option key={index} value={option.deal_id}>
                    {option.deal_name +
                      " | " +
                      option.deal_id +
                      " | " +
                      option.deal_owner_name}
                  </Option>
                ))}
            </Select>
          </Col>
        </>
      )}
      {selectedDealId && (
        <>
          {selectedDealDetails?.has_pricing_deal && (
            <Col span={24} style={{ marginTop: "8px" }}>
              <Tag icon={<ExclamationCircleOutlined />} color="orange">
                Deal already has pricing version(s) created. Changes to the
                hubspot values will be reflected across all created versions.
              </Tag>
            </Col>
          )}
          <Col span={24}>
            <br />
            <Row gutter={16}>
              <Col span={9}>
                <p>
                  <span style={{ fontWeight: "bold" }}>Deal ID : </span>
                  <a
                    target="_blank"
                    href={selectedDealDetails?.hubspot_link ?? "#"}
                  >
                    {selectedDealDetails?.deal_id}
                  </a>
                </p>
              </Col>
              <Col span={13}>
                <p>
                  <span style={{ fontWeight: "bold" }}>Deal Name : </span>
                  {selectedDealDetails?.deal_name}
                </p>
              </Col>
              <Col span={9}>
                <p>
                  <span style={{ fontWeight: "bold" }}>Deal Owner : </span>
                  {selectedDealDetails?.deal_owner_name}
                </p>
              </Col>
              <Col span={13}>
                <p>
                  <span style={{ fontWeight: "bold" }}>Deal Client : </span>
                  {selectedDealDetails?.company_name}
                </p>
              </Col>
            </Row>
          </Col>
        </>
      )}
      {selectedDealId && (
        <>
          <Col span={24}>
            <Tag icon={<ExclamationCircleOutlined />} color="warning">
              <strong>Note:</strong> The following properties will be
              overwritten in HubSpot with values from the pricing template.
            </Tag>
          </Col>
          <Col span={24} style={{ marginTop: "6px" }}>
            <Table
              columns={columns}
              dataSource={generateDiffData(pricingTemplateData, selectedDealId)}
              pagination={false}
              bordered
            />
          </Col>

          <Col span={24} style={{ marginTop: "8px" }}>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={() => closeModal()} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => createDealPricingFromTemplate()}
                loading={submitting}
              >
                Confirm
              </Button>
            </div>
          </Col>
        </>
      )}
    </Row>
  );
}
